import React, { useCallback, useEffect, useState } from 'react';
import { Card, Form, Select, InputNumber, Button, Space, message, Alert, Typography } from 'antd';
import { DollarCircleOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import MainLayout from '../../layout/layout';
import { apiClient } from '../../../config/apiClient';

const { Text } = Typography;

const SeniaClub = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modoCobro, setModoCobro] = useState('TOTAL');
  const [seniaHabilitada, setSeniaHabilitada] = useState(true);

  const loadEstadoModulo = useCallback(async () => {
    try {
      const response = await apiClient.get('/modulos/config');
      if (!response.ok) {
        setSeniaHabilitada(true);
        return;
      }
      const data = await response.json();
      setSeniaHabilitada(data?.clubSenia !== false);
    } catch (error) {
      console.error('Error al cargar estado del modulo seña:', error);
      setSeniaHabilitada(true);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/club/config-senia');
      if (!response.ok) {
        const err = await response.json();
        message.error(err.message || 'No se pudo cargar la configuracion de seña');
        return;
      }
      const data = await response.json();
      const modo = String(data?.modoCobro || 'TOTAL').toUpperCase();
      const porcentaje = Number(data?.porcentajeSenia ?? 100);
      const minutos = Number(data?.minutosEsperaPago ?? 0);

      setModoCobro(modo);
      form.setFieldsValue({
        modoCobro: modo,
        porcentajeSenia: Number.isNaN(porcentaje) ? 100 : porcentaje,
        minutosEsperaPago: Number.isNaN(minutos) ? 0 : minutos,
      });
    } catch (error) {
      console.error('Error al cargar configuracion de seña:', error);
      message.error('Error al cargar configuracion de seña');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    loadEstadoModulo();
    loadConfig();
  }, [loadEstadoModulo, loadConfig]);

  const onValuesChange = (changedValues) => {
    if (changedValues.modoCobro) {
      const nuevoModo = String(changedValues.modoCobro).toUpperCase();
      setModoCobro(nuevoModo);
      if (nuevoModo === 'TOTAL') {
        form.setFieldValue('porcentajeSenia', 100);
      }
    }
  };

  const handleGuardar = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        modoCobro: values.modoCobro,
        porcentajeSenia: Number(values.porcentajeSenia),
        minutosEsperaPago: Number(values.minutosEsperaPago),
      };
      const response = await apiClient.put('/club/config-senia', payload);
      if (!response.ok) {
        const err = await response.json();
        message.error(err.message || 'No se pudo guardar la configuracion');
        return;
      }
      message.success('Configuracion de seña guardada correctamente');
      loadConfig();
    } catch (error) {
      if (error?.errorFields) return;
      console.error('Error al guardar configuracion de seña:', error);
      message.error('Error al guardar configuracion de seña');
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout>
      <Card
        title={(
          <Space>
            <DollarCircleOutlined />
            <span>Seña</span>
          </Space>
        )}
        extra={(
          <Button icon={<ReloadOutlined />} onClick={loadConfig} loading={loading} disabled={!seniaHabilitada}>
            Actualizar
          </Button>
        )}
        loading={loading}
      >
        {!seniaHabilitada && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Para activar esta opcion debe solicitar el servicio a la empresa"
          />
        )}
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Configura cuánto se cobra online y por cuánto tiempo se reserva el turno mientras el pago está pendiente."
        />

        <div
          style={{
            filter: seniaHabilitada ? 'none' : 'blur(2px)',
            opacity: seniaHabilitada ? 1 : 0.6,
            pointerEvents: seniaHabilitada ? 'auto' : 'none',
            userSelect: seniaHabilitada ? 'auto' : 'none',
            transition: 'all 0.2s ease',
          }}
        >
          <Form
            form={form}
            layout="vertical"
            onValuesChange={onValuesChange}
            initialValues={{
              modoCobro: 'TOTAL',
              porcentajeSenia: 100,
              minutosEsperaPago: 0,
            }}
          >
            <Form.Item
              label="Modo de cobro online"
              name="modoCobro"
              rules={[{ required: true, message: 'Selecciona un modo de cobro' }]}
            >
              <Select
                options={[
                  { value: 'TOTAL', label: 'Cobrar el total del turno (100%)' },
                  { value: 'PORCENTAJE', label: 'Cobrar una seña en porcentaje' },
                ]}
              />
            </Form.Item>

            <Form.Item
              label="Porcentaje de seña"
              name="porcentajeSenia"
              rules={[
                { required: true, message: 'Ingresa el porcentaje de seña' },
                {
                  validator: (_, value) => {
                    const num = Number(value);
                    if (Number.isNaN(num) || num <= 0 || num > 100) {
                      return Promise.reject(new Error('Debe ser mayor a 0 y menor o igual a 100'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              extra={modoCobro === 'TOTAL' ? 'En modo total queda fijo en 100%.' : 'Ejemplo: 10 para cobrar el 10%.'}
            >
              <InputNumber min={0.01} max={100} step={0.1} precision={2} style={{ width: 240 }} disabled={modoCobro === 'TOTAL'} addonAfter="%" />
            </Form.Item>

            <Form.Item
              label="Minutos de espera por pago pendiente"
              name="minutosEsperaPago"
              rules={[
                { required: true, message: 'Ingresa los minutos de espera' },
                {
                  validator: (_, value) => {
                    const num = Number(value);
                    if (!Number.isInteger(num) || num < 0 || num > 1440) {
                      return Promise.reject(new Error('Debe ser un entero entre 0 y 1440'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              extra="Si se cumple este tiempo y el pago no se acredita, la reserva se cancela y el turno vuelve a estar disponible. Usa 0 para no vencer automaticamente."
            >
              <InputNumber min={0} max={1440} step={1} style={{ width: 240 }} addonAfter="min" />
            </Form.Item>

            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              Nota: el pago se confirma cuando Mercado Pago informa estado aprobado.
            </Text>

            <Space>
              <Button type="primary" icon={<SaveOutlined />} onClick={handleGuardar} loading={saving}>
                Guardar
              </Button>
              <Button onClick={loadConfig} disabled={saving}>
                Restablecer desde servidor
              </Button>
            </Space>
          </Form>
        </div>
      </Card>
    </MainLayout>
  );
};

export default SeniaClub;
